/**
 * Los cinco pasos de una atención, mostrando dónde está el paciente.
 *
 * MISMO DIBUJO QUE LA GUÍA, SOLO QUE VIVO
 *
 * Es la tira de siempre —mismos iconos, mismo círculo, misma flecha entre
 * pasos— y por eso se reconoce al instante. Lo único que cambia es que aquí
 * dice algo de ESTE paciente:
 *
 * - Cumplido: el círculo se llena del color de marca y el icono queda en
 *   blanco. El icono sigue viéndose —que era el problema de taparlo con un
 *   check— y el paso se distingue de lejos, sin insignias que muerdan el borde.
 * - Actual: aro completo y etiqueta en negrita. Es lo único que hace falta
 *   resaltar; lo demás no debe competir con él.
 * - Pendiente: EXACTAMENTE igual que en la guía. Un mismo paso no puede verse
 *   de un color en una pantalla y de otro en la siguiente.
 */
import {
  Activity,
  ArrowRight,
  Calendar,
  FileSignature,
  Stethoscope,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface Paso {
  n: number;
  label: string;
  icono: LucideIcon;
}

const PASOS: Paso[] = [
  { n: 1, label: "Cita", icono: Calendar },
  { n: 2, label: "Pago (Caja)", icono: Wallet },
  { n: 3, label: "Triaje", icono: Activity },
  { n: 4, label: "Consulta", icono: Stethoscope },
  { n: 5, label: "Dx + Receta", icono: FileSignature },
];

export function FlujoAtencion({
  paso,
  terminado = false,
  cancelada = false,
  sinTriaje = false,
  label,
}: {
  /** Paso en el que está: 2 = por cobrar, 3 = triaje, 4 = consulta, 5 = receta. */
  paso: number;
  /** La atención ya se cerró: todos los pasos cumplidos. */
  terminado?: boolean;
  cancelada?: boolean;
  /** Sesión de un plan: no pasa por triaje y el paso no se dibuja. */
  sinTriaje?: boolean;
  /** El estado en palabras, el mismo del badge. */
  label?: string;
}) {
  // En una sesión de tratamiento el triaje no se salta ni se posterga: no
  // aplica. Dibujarlo en gris haría pensar que falta hacerlo.
  const pasos = sinTriaje ? PASOS.filter((p) => p.n !== 3) : PASOS;
  return (
    <div>
      {/* py-2: el aro del paso actual sobresale del círculo y este contenedor
          desplaza en horizontal, así que sin margen ARRIBA lo recortaba. */}
      <div className="flex items-center gap-1 overflow-x-auto px-0.5 py-2">
        {pasos.map((p, i) => {
          const hecho = cancelada ? false : terminado || p.n < paso;
          const actual = !cancelada && !terminado && p.n === paso;
          const Icono = p.icono;
          return (
            <div key={p.label} className="flex shrink-0 items-center gap-1">
              <div className="flex flex-col items-center gap-1.5 px-2">
                <div
                  className={[
                    "flex h-10 w-10 items-center justify-center rounded-full transition",
                    hecho
                      ? // Cumplido: círculo lleno del color de marca, con su
                        // icono en blanco. Se lee de lejos y el icono sigue
                        // ahí — nada de insignias encima que muerdan el borde.
                        "bg-[color:var(--color-link)] text-white"
                      : "bg-[color-mix(in_srgb,var(--color-link)_8%,white)] text-[color:var(--color-link)]",
                    actual
                      ? "ring-2 ring-[color:var(--color-link)] ring-offset-2"
                      : "",
                  ].join(" ")}
                >
                  <Icono className="h-5 w-5" />
                </div>
                <span
                  className={[
                    "whitespace-nowrap text-xs",
                    actual
                      ? "font-semibold text-[color:var(--color-link)]"
                      : "font-medium text-slate-600",
                  ].join(" ")}
                >
                  {p.label}
                </span>
              </div>
              {i < pasos.length - 1 && (
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" />
              )}
            </div>
          );
        })}
      </div>
      {label && (
        <p className="mt-2 text-xs text-slate-500">
          Ahora:{" "}
          <span
            className={
              cancelada
                ? "font-semibold text-red-600"
                : "font-semibold text-[color:var(--color-link)]"
            }
          >
            {label}
          </span>
        </p>
      )}
    </div>
  );
}

/** Atajo para cuando se tiene la cita entera. */
export function FlujoAtencionDeCita({
  cita,
  tieneTriaje = false,
}: {
  cita: {
    estado_flujo: string;
    estado_flujo_label: string;
    estado_flujo_paso: number;
    triaje_requerido?: boolean;
    plan_id?: number | null;
  };
  /** Si se le tomó de todas formas: entonces el paso sí se dibuja. */
  tieneTriaje?: boolean;
}) {
  // Quién no pasa por triaje ya no es solo "sesión de un plan": también las
  // derivaciones y las especialidades que no trian. Lo decide el servidor con
  // una sola regla (`triaje_requerido`) y aquí solo se dibuja. El `plan_id` se
  // conserva de respaldo para las pantallas que aún no piden el campo.
  const noAplica = cita.triaje_requerido === false || cita.plan_id != null;
  return (
    <FlujoAtencion
      paso={cita.estado_flujo_paso}
      terminado={cita.estado_flujo === "ATENDIDA"}
      cancelada={cita.estado_flujo === "CANCELADA" || cita.estado_flujo === "NO_ASISTIO"}
      sinTriaje={noAplica && !tieneTriaje}
      label={cita.estado_flujo_label}
    />
  );
}
